import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { inputClass } from '../../components/ui/Field';
import { attendanceSummary, statusBar } from '../../utils/attendance';
import { fullName, voiceClass, voiceLabel, voiceOrder } from '../../utils/labels';
import type { Voice } from '../../types';
export function AdminAttendanceReport() {
  const {
    members,
    settings
  } = useApp();
  const [glas, setGlas] = useState<Voice | 'vsi'>('vsi');
  const [sezona, setSezona] = useState('2026/27');
  const [samoPodPragom, setSamoPodPragom] = useState(false);
  const vrstice = useMemo(() => {
    return members.filter(m => m.type === 'aktivni' || m.type === 'gostujoci').filter(m => glas === 'vsi' ? true : m.voice === glas).map(m => ({
      m,
      s: attendanceSummary(m, settings)
    })).filter(r => samoPodPragom ? r.s.status === 'oranzna' || r.s.status === 'rdeca' : true).sort((a, b) => (a.s.odstotek ?? 999) - (b.s.odstotek ?? 999));
  }, [members, settings, glas, samoPodPragom]);
  const skupine = useMemo(() => {
    const aktivni = members.filter(m => m.type === 'aktivni');
    const acc = {
      zelena: 0,
      oranzna: 0,
      rdeca: 0,
      premalo: 0
    };
    aktivni.forEach(m => {
      acc[attendanceSummary(m, settings).status] += 1;
    });
    const povprecje = Math.round(aktivni.reduce((sum, m) => sum + (m.pricakovan ? m.prisoten / m.pricakovan : 0), 0) / Math.max(aktivni.filter(m => m.pricakovan > 0).length, 1) * 100);
    return {
      ...acc,
      povprecje
    };
  }, [members, settings]);
  return <div className="space-y-5" data-id="element-613" data-mp="1emfbjc61mnt9">
      <div data-id="element-614" data-mp="1enqdd6fxhlhq">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-615" data-mp="19msoyc4brv7s">Poročilo o prisotnosti</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-616" data-mp="19p5cr5wrkzsr">
          Semafor se vklopi po {settings.minDogodkov} štetih dogodkih. Opravičene odsotnosti se štejejo kot
          odsotnost.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-id="element-617" data-mp="28bmg5xodwzva">
        {[{
        label: 'Povprečna prisotnost',
        value: `${skupine.povprecje} %`,
        cls: 'text-ink'
      }, {
        label: 'V redu',
        value: skupine.zelena,
        cls: 'text-green-600'
      }, {
        label: 'Na meji',
        value: skupine.oranzna,
        cls: 'text-amber-600'
      }, {
        label: 'Pod pragom',
        value: skupine.rdeca,
        cls: 'text-rose-600'
      }, {
        label: 'Premalo podatkov',
        value: skupine.premalo,
        cls: 'text-ink-muted'
      }].map(s => <Card key={s.label} className="p-4" data-id="element-618" data-mp="2w1qj02tr0vl8">
            <p className={`text-2xl font-semibold ${s.cls}`} data-id="element-619" data-mp="2w0fg0584nrv6">{s.value}</p>
            <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-620" data-mp="311cvga1r67sz">{s.label}</p>
          </Card>)}
      </div>

      <Card className="p-4" data-id="element-621" data-mp="36068k1d4nwsm">
        <div className="grid gap-3 sm:grid-cols-3" data-id="element-622" data-mp="35xa9uatgh434">
          <select value={sezona} onChange={e => setSezona(e.target.value)} aria-label="Sezona" className={inputClass} data-id="element-623" data-mp="ud2ruv487c3g">
            <option data-id="element-624" data-mp="kdbyiwrej34y">2026/27</option>
            <option data-id="element-625" data-mp="kedkvxdvbc1z">2025/26</option>
          </select>
          <select value={glas} onChange={e => setGlas(e.target.value as Voice | 'vsi')} aria-label="Glasovna skupina" className={inputClass} data-id="element-626" data-mp="ff0nwjta8olo">
            <option value="vsi" data-id="element-627" data-mp="182wi9hvdq4fa">Vse glasovne skupine</option>
            {voiceOrder.map(v => <option key={v} value={v} data-id="element-628" data-mp="13c7plz7yr1ek">
                {voiceLabel[v]}
              </option>)}
          </select>
          <label className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-[13px] text-ink" data-id="element-629" data-mp="ycv4biwqq2e3">
            <input type="checkbox" checked={samoPodPragom} onChange={e => setSamoPodPragom(e.target.checked)} className="h-4 w-4 rounded border-line accent-green-500" data-id="element-630" data-mp="1vulidkefzx6s" />
            Prikaži samo člane pod pragom
          </label>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-631" data-mp="1gh0yayqw5yuk">
        <div className="overflow-x-auto viva-scroll" data-id="element-632" data-mp="11mwftfq00ezu">
          <table className="w-full min-w-[820px] text-left text-sm" data-id="element-633" data-mp="11oh7mxkxhju0">
            <thead className="border-b border-line bg-canvas text-[12px] uppercase tracking-wide text-ink-muted" data-id="element-634" data-mp="16lprd2lq5vxd">
              <tr data-id="element-635" data-mp="1ucv7zjzhos8q">
                <th className="px-5 py-3 font-medium" data-id="element-636" data-mp="1z9ve1uwc9uzu">Član</th>
                <th className="px-4 py-3 font-medium" data-id="element-637" data-mp="1z9bwifalp8mc">Glas</th>
                <th className="px-4 py-3 font-medium" data-id="element-638" data-mp="1klid0jie9lyh">Prisoten / pričakovan</th>
                <th className="px-4 py-3 font-medium" data-id="element-639" data-mp="1pjsbp43bv4ou">Odstotek</th>
                <th className="px-4 py-3 font-medium" data-id="element-640" data-mp="1pi8rj2ejw9hs">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-641" data-mp="2d7jnm6kyhfpl">
              {vrstice.map(({
              m,
              s
            }) => <tr key={m.id} className="transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-642" data-mp="2i2frq53gvtj2">
                  <td className="px-5 py-3" data-id="element-643" data-mp="6jaxwpk6dpvm">
                    <Link to={`/admin/clani/${m.id}`} className="flex items-center gap-3" data-id="element-644" data-mp="1n2ntqku4mvb">
                      <Avatar member={m} size="sm" data-id="element-645" data-mp="gbp1zoyq78ky" />
                      <span data-id="element-646" data-mp="gadw9jlpzqr7">
                        <span className="block font-medium text-ink" data-id="element-647" data-mp="bc45dl713cbe">{fullName(m)}</span>
                        {m.type === 'gostujoci' && <span className="block text-[12px] text-purple-600" data-id="element-648" data-mp="3jsp325u7miz7">Gostujoči član</span>}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3" data-id="element-649" data-mp="3stdgozfoyhbk">
                    <Badge className={voiceClass[m.voice]} data-id="element-650" data-mp="3or0doisbwxih">{voiceLabel[m.voice]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-muted" data-id="element-651" data-mp="18238v18bnkn4">
                    {s.prisoten} / {s.pricakovan}
                  </td>
                  <td className="px-4 py-3" data-id="element-652" data-mp="13zq70nwp46vm">
                    <div className="flex items-center gap-2" data-id="element-653" data-mp="143471phw03zw">
                      <div className="h-1.5 w-28 overflow-hidden rounded-full bg-canvas" data-id="element-654" data-mp="p2k67wa443g">
                        <div className={`h-full rounded-full ${statusBar[s.status]}`} style={{
                      width: `${s.odstotek ?? 0}%`
                    }} data-id="element-655" data-mp="5nvvbto3fj0g" />
                      </div>
                      <span className="w-10 text-[13px] font-medium text-ink" data-id="element-656" data-mp="enj10rnuvazb">
                        {s.odstotek === null ? '—' : `${s.odstotek}%`}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-657" data-mp="3n5ghjdcyt1bd">
                    <StatusBadge status={s.status} odstotek={s.odstotek} showPercent={false} data-id="element-658" data-mp="3n4nbyfpqshut" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}